import type { AuthAction } from "@/context/authContext";

export const BASE_URL = import.meta.env.VITE_SERVER_URL;
const STORAGE_KEY = "friexp.ui";

function readStoredAccessToken(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { accessToken?: string | null };
    return parsed?.accessToken ?? null;
  } catch {
    return null;
  }
}

let accessToken: string | null = readStoredAccessToken();

export const setAccessToken = (t: string | null) => {
  accessToken = t;
};

type Options = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown;
  headers?: HeadersInit;
  responseType?: "json" | "blob" | "text" | "raw" | "file";
};

function buildHeaders(init?: HeadersInit) {
  const h = new Headers();
  if (init instanceof Headers) init.forEach((v, k) => h.set(k, v));
  else if (Array.isArray(init)) for (const [k, v] of init) h.set(k, v);
  else if (init)
    for (const [k, v] of Object.entries(init)) h.set(k, v as string);
  if (!h.has("Content-Type")) h.set("Content-Type", "application/json");
  if (accessToken) h.set("Authorization", `Bearer ${accessToken}`);
  return h;
}

async function refreshAccessToken(
  dispatch?: React.Dispatch<AuthAction>,
): Promise<string | null> {
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) throw new Error("Failed to refresh token");
    const data = await res.json();
    const newToken = data.accessToken as string | undefined;
    if (newToken) {
      setAccessToken(newToken);
      if (dispatch) {
        dispatch({ type: "SET_ACCESS_TOKEN", payload: newToken });
        dispatch({
          type: "UPDATE_USER",
          payload: data.user,
        });
      }
      return newToken;
    }
    return null;
  } catch (err) {
    console.error("Error refreshing access token", err);
    return null;
  }
}

export async function api<T = any>(
  path: string,
  opts: Options = {},
  dispatch?: React.Dispatch<AuthAction>,
): Promise<T> {
  const { responseType = "json" } = opts;
  const headers = buildHeaders(opts.headers);

  let res = await fetch(`${BASE_URL}${path}`, {
    ...opts,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: "include",
  });

  if (res.status === 401 && dispatch) {
    const newToken = await refreshAccessToken(dispatch);
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      res = await fetch(`${BASE_URL}${path}`, {
        ...opts,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        credentials: "include",
      });
    } else {
      dispatch({ type: "LOGOUT" });
      throw new Error("Unauthorized — please log in again");
    }
  }

  if (!res.ok) {
    let msg = res.statusText;
    try {
      const data = await res.json();
      msg = data?.message || msg;
    } catch {}
    throw new Error(msg);
  }

  if (res.status === 204) return null as T;

  switch (responseType) {
    case "blob":
      return res.blob() as T;
    case "text":
      return res.text() as T;
    case "raw":
      return res as T;
    case "file": {
      const disposition = res.headers.get("Content-Disposition") || "";
      let filename = "download";

      const filenameMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i);
      if (filenameMatch?.[1]) filename = filenameMatch[1];

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);

      return filename as T;
    }
    case "json":
    default:
      return res.json();
  }
}
